"use client";

import type { ReactNode } from "react";
import { Tabs } from "react-aria-components";

import PageSection from "@ui/components/composers/PageSection";
import { PricingCardsListV3 } from "@ui/components/pricing/PricingCardsListV3";
import { PricingControlsV3 } from "@ui/components/pricing/PricingControlsV3";
import type { PlatformVariant } from "@ui/components/pricing/PricingControlsV3";
import { usePricingTableContext } from "@ui/components/pricing/PricingTableContext";
import { EGGSHELL } from "@ui/tokens/pageColors";

export type PricingDisplay = "tiers" | "agent-calls";

interface PricingCardsV3ClientProps {
  showStarterPromo?: boolean;
  variant?: "unified" | "split";
  markerBackgroundColor?: string;
  showPlatformSelector?: boolean;
  /** Hide all controls (platform tabs and billing selector). Use when controls are rendered elsewhere. */
  hideControls?: boolean;
  /** Controls the border/decoration style of the pricing sections. Use 'none' to hide sidelines and edge lines. */
  borderStyle?: "default" | "none";
  /** Controls what price to display: "tiers" shows tier price, "agent-calls" shows hosting cost per minute */
  pricingDisplay?: PricingDisplay;
  /** Whether to show the footer with pricing notice. Defaults to true. */
  showFooter?: boolean;
  /** Whether to show the credits included section at the bottom of each card. Defaults to true. */
  showCreditsIncluded?: boolean;
}

/** Reusable PageSection wrapper with common pricing card section props */
const CardsSection = ({
  children,
  showBorders,
  markerBackgroundColor,
}: {
  children: ReactNode;
  showBorders: boolean;
  markerBackgroundColor: string;
}) => (
  <PageSection
    withSideLines={showBorders}
    withFilledLine
    topPadding='none'
    bottomPadding='none'
    horizontalPadding='none'
    withGap={false}
    markerBackgroundColor={markerBackgroundColor}
    withEdgeLines={showBorders}
  >
    {children}
  </PageSection>
);

export const PricingCardsV3Client = ({
  showStarterPromo = false,
  variant = "unified",
  markerBackgroundColor = EGGSHELL,
  showPlatformSelector = true,
  hideControls = false,
  borderStyle = "default",
  pricingDisplay = "tiers",
  showFooter = true,
  showCreditsIncluded = true,
}: PricingCardsV3ClientProps) => {
  const { selectedPlatform, setSelectedPlatform } = usePricingTableContext();

  const showBorders = borderStyle !== "none";
  const sectionProps = { showBorders, markerBackgroundColor };

  // Render split layout (individual + business tiers separately)
  const renderSplitContent = (withoutTabs: boolean) => (
    <>
      <CardsSection {...sectionProps}>
        <PricingCardsListV3
          showStarterPromo={showStarterPromo}
          filterTiers='individual'
          showFooter={false}
          withoutTabs={withoutTabs}
          pricingDisplay={pricingDisplay}
          showCreditsIncluded={showCreditsIncluded}
        />
      </CardsSection>
      <CardsSection {...sectionProps}>
        <PricingCardsListV3
          showStarterPromo={false}
          filterTiers='business'
          showFooter={true}
          withoutTabs={withoutTabs}
          pricingDisplay={pricingDisplay}
          showCreditsIncluded={showCreditsIncluded}
        />
      </CardsSection>
    </>
  );

  // Render unified layout (all tiers together)
  const renderUnifiedContent = (withoutTabs: boolean) => (
    <CardsSection {...sectionProps}>
      <PricingCardsListV3
        showStarterPromo={showStarterPromo}
        withoutTabs={withoutTabs}
        pricingDisplay={pricingDisplay}
        showFooter={showFooter}
        showCreditsIncluded={showCreditsIncluded}
      />
    </CardsSection>
  );

  // When controls are hidden, parent handles tabs - render content directly without Tabs wrapper
  if (hideControls) {
    return (
      <div className='container'>{variant === "split" ? renderSplitContent(true) : renderUnifiedContent(true)}</div>
    );
  }

  return (
    <div className='container'>
      <Tabs selectedKey={selectedPlatform} onSelectionChange={key => setSelectedPlatform(key as PlatformVariant)}>
        <div className={!showBorders ? "tw-pr-2 md:tw-pr-4" : undefined}>
          <PricingControlsV3 showPlanTypeSelector={variant !== "split"} showPlatformSelector={showPlatformSelector} />
        </div>
        {variant === "split" ? renderSplitContent(false) : renderUnifiedContent(false)}
      </Tabs>
    </div>
  );
};
